import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { FacilityRoles, FacilityUserPermissions } from "@src/appV2/FacilityUser/types";
import { includes } from "lodash";

import { useSession } from "../api/useSession";

/**
 * A unified hook for checking user permissions that correctly handles pseudo-login sessions.
 *
 * Key Concepts:
 * - `hasAdminPrivileges`: User has CBH admin privileges (persists during pseudo-login)
 * - `isCurrentlyAdmin`: User is currently logged in as admin (false during pseudo-login)
 * - `isInPseudoSession`: Admin is currently logged in as another user type
 *
 * @example
 * const { canAccessDailyView } = useEffectivePermissions();
 * if (canAccessDailyView) { ... }
 */
// eslint-disable-next-line complexity
export function useEffectivePermissions() {
  const session = useSession();
  const { admin, adminProfile, user, type } = session ?? {};
  const { access: roles = [], permissions = [] } = user ?? {};

  // Core identity flags
  const hasAdminPrivileges = Boolean(admin);
  const isCurrentlyAdmin = type === "ADMIN";
  const isCurrentlyWorkplace = type === "FACILITY";
  const isCurrentlyWorker = type === "AGENT";

  // Pseudo-session detection
  const isInPseudoSession = hasAdminPrivileges && !isCurrentlyAdmin;
  const originalUserId = session?.adminUserId;

  // Helper functions
  const hasWorkplaceRole = (role: FacilityRoles): boolean =>
    hasAdminPrivileges || includes(roles, role);

  const hasWorkplacePermission = (permission: FacilityUserPermissions): boolean =>
    hasAdminPrivileges || includes(permissions, permission);

  const hasAnyWorkplaceRole = (checkRoles: FacilityRoles[]): boolean =>
    hasAdminPrivileges || checkRoles.some((role) => includes(roles, role));

  const hasAnyWorkplacePermission = (checkPermissions: FacilityUserPermissions[]): boolean =>
    hasAdminPrivileges || checkPermissions.some((perm) => includes(permissions, perm));

  const hasAdminRole = (role: string): boolean =>
    hasAdminPrivileges && (adminProfile?.roles?.includes(role) ?? false);

  // Common permission combinations
  const hasDefaultWorkplacePermissions = hasAnyWorkplaceRole([
    FacilityRoles.ADMIN,
    FacilityRoles.SHIFT_MANAGEMENT,
    FacilityRoles.DOCUMENTS,
    FacilityRoles.INVOICES,
    FacilityRoles.CORPORATE_ADMIN,
  ]);

  const canManageShifts =
    hasAdminPrivileges ||
    hasWorkplaceRole(FacilityRoles.SHIFT_MANAGEMENT) ||
    hasWorkplaceRole(FacilityRoles.ADMIN);

  const canViewBilling =
    hasAdminPrivileges ||
    hasWorkplaceRole(FacilityRoles.ADMIN) ||
    hasWorkplaceRole(FacilityRoles.INVOICES) ||
    hasWorkplacePermission(FacilityUserPermissions.MANAGE_INVOICES);

  const canAccessDailyView = hasDefaultWorkplacePermissions;

  const canEditDocuments =
    hasAdminPrivileges ||
    hasAdminRole(EMPLOYEE_ROLES.DOCUMENTS) ||
    hasAdminRole(EMPLOYEE_ROLES.DOCUMENT_CREATION) ||
    (isCurrentlyWorkplace &&
      hasAnyWorkplaceRole([
        FacilityRoles.ADMIN,
        FacilityRoles.SHIFT_MANAGEMENT,
        FacilityRoles.DOCUMENTS,
      ]));

  const canUploadDeleteDocuments =
    hasAdminRole(EMPLOYEE_ROLES.DOCUMENTS) || hasAdminRole(EMPLOYEE_ROLES.DOCUMENT_CREATION);

  const canViewWorkplaceChat =
    (hasAdminPrivileges && hasAdminRole(EMPLOYEE_ROLES.VIEW_WORKPLACE_CHAT)) ||
    hasWorkplaceRole(FacilityRoles.ADMIN) ||
    hasWorkplaceRole(FacilityRoles.DOCUMENTS) ||
    hasWorkplaceRole(FacilityRoles.SHIFT_MANAGEMENT);

  const canAccessWorkerDirectory =
    hasAdminPrivileges ||
    hasWorkplaceRole(FacilityRoles.ADMIN) ||
    hasWorkplaceRole(FacilityRoles.SHIFT_MANAGEMENT) ||
    hasWorkplaceRole(FacilityRoles.RECRUITMENT);

  // Billing and invoice permissions
  const hasBillingRole = hasAdminRole(EMPLOYEE_ROLES.BILLING);
  const hasAdministrativeRole = hasAdminRole(EMPLOYEE_ROLES.ADMINISTRATIVE);
  const hasBillingOrAdministrativeRole = hasBillingRole || hasAdministrativeRole;

  const canImpersonateUsers = hasAdminRole(EMPLOYEE_ROLES.IMPERSONATION);

  const canGetWorkplaceUserMagicLink = hasAdminRole(EMPLOYEE_ROLES.GET_WORKPLACE_USER_MAGIC_LINK);

  const canResetWorkplaceUserPassword = hasAdminRole(EMPLOYEE_ROLES.RESET_USER_PASSWORD);

  const canLogoutUserFromEverywhere = hasAdminRole(EMPLOYEE_ROLES.GLOBAL_USER_LOGOUT);

  const canAccessSyncRuns =
    Boolean(session) &&
    (hasAdminPrivileges ? hasAdminRole(EMPLOYEE_ROLES.SHIFT_MANAGEMENT) : isCurrentlyWorkplace);

  const canViewInvoices =
    hasAdminPrivileges ||
    hasWorkplaceRole(FacilityRoles.ADMIN) ||
    hasWorkplaceRole(FacilityRoles.INVOICES) ||
    hasWorkplacePermission(FacilityUserPermissions.MANAGE_INVOICES);

  const canEditInvoices =
    (hasAdminPrivileges && (hasAdministrativeRole || hasAdminRole(EMPLOYEE_ROLES.INVOICE_EDIT))) ||
    canViewInvoices;

  return {
    // Core identity flags
    hasAdminPrivileges,
    isCurrentlyAdmin,
    isCurrentlyWorkplace,
    isCurrentlyWorker,

    // Pseudo-session detection
    isInPseudoSession,
    originalUserId,

    // Helper methods
    hasWorkplaceRole,
    hasWorkplacePermission,
    hasAnyWorkplaceRole,
    hasAnyWorkplacePermission,
    hasAdminRole,

    // Common permission flags
    hasDefaultWorkplacePermissions,
    canManageShifts,
    canViewBilling,
    canAccessDailyView,
    canEditDocuments,
    canUploadDeleteDocuments,
    canViewWorkplaceChat,
    canAccessWorkerDirectory,
    hasBillingRole,
    hasAdministrativeRole,
    hasBillingOrAdministrativeRole,
    canImpersonateUsers,
    canGetWorkplaceUserMagicLink,
    canResetWorkplaceUserPassword,
    canLogoutUserFromEverywhere,
    canAccessSyncRuns,
    canViewInvoices,
    canEditInvoices,

    // Raw data (for custom checks)
    roles,
    permissions,
    adminRoles: adminProfile?.roles ?? [],
  };
}
